
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { featuredArticles, recentArticles } from '../../../mocks/articles';

interface Article {
  id: number;
  title: string;
  excerpt: string;
  content?: string;
  category: string;
  author: string;
  date: string;
  views: number;
  image: string;
  mainImage?: string;
  sidebarImage?: string;
  tags?: string[];
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [allArticles, setAllArticles] = useState<Article[]>([]);
  const [selectedArticles, setSelectedArticles] = useState<number[]>([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteMessage, setDeleteMessage] = useState('');

  // 管理者認証チェック
  useEffect(() => {
    const adminSession = localStorage.getItem('adminSession');
    if (!adminSession) {
      navigate('/admin/login');
      return;
    }

    try {
      const session = JSON.parse(adminSession);
      if (!session.isAdmin) {
        navigate('/admin/login');
        return;
      }
    } catch {
      navigate('/admin/login');
      return;
    }

    loadArticles();
  }, [navigate]);

  const loadArticles = () => {
    const userArticles = JSON.parse(localStorage.getItem('userArticles') || '[]');
    const combinedArticles = [...userArticles, ...featuredArticles, ...recentArticles];
    setAllArticles(combinedArticles);
  };

  const handleLogout = () => {
    localStorage.removeItem('adminSession');
    navigate('/admin/login');
  };

  const handleSelectArticle = (articleId: number) => {
    setSelectedArticles(prev => {
      if (prev.includes(articleId)) {
        return prev.filter(id => id !== articleId);
      } else {
        return [...prev, articleId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedArticles.length === allArticles.length) {
      setSelectedArticles([]);
    } else {
      setSelectedArticles(allArticles.map(article => article.id));
    }
  };

  const handleDeleteSelected = () => {
    if (selectedArticles.length === 0) {
      setDeleteMessage('削除する記事を選択してください。');
      return;
    }
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    // ユーザー投稿記事のみ削除可能
    const userArticles = JSON.parse(localStorage.getItem('userArticles') || '[]');
    const updatedUserArticles = userArticles.filter((article: Article) => 
      !selectedArticles.includes(article.id)
    );
    
    localStorage.setItem('userArticles', JSON.stringify(updatedUserArticles));
    
    // 記事更新イベントを発火
    window.dispatchEvent(new CustomEvent('articleUpdated'));
    
    setDeleteMessage(`${selectedArticles.length}件の記事を削除しました。`);
    setSelectedArticles([]);
    setShowDeleteConfirm(false);
    loadArticles();
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
  };

  // ユーザー投稿記事かどうかを判定
  const isUserArticle = (articleId: number) => {
    const userArticles = JSON.parse(localStorage.getItem('userArticles') || '[]');
    return userArticles.some((article: Article) => article.id === articleId);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-gray-900 text-white py-4">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              <h1 className="text-2xl font-bold">管理者ダッシュボード</h1>
              <span className="text-blue-400 text-lg">記事管理システム</span>
            </div>
            
            <nav className="flex items-center space-x-6">
              <button 
                onClick={() => navigate('/media')}
                className="hover:text-blue-400 transition-colors cursor-pointer"
              >
                メディアホーム
              </button>
              <button 
                onClick={handleLogout}
                className="bg-red-600 hover:bg-red-700 px-4 py-2 rounded-full transition-colors cursor-pointer whitespace-nowrap"
              >
                ログアウト
              </button>
            </nav>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-xl shadow-lg">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <i className="ri-article-line text-2xl text-blue-600"></i>
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-600">総記事数</p>
                <p className="text-2xl font-bold text-gray-900">{allArticles.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-lg">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <i className="ri-user-add-line text-2xl text-green-600"></i>
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-600">ユーザー投稿</p>
                <p className="text-2xl font-bold text-gray-900">
                  {JSON.parse(localStorage.getItem('userArticles') || '[]').length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-lg">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                <i className="ri-star-line text-2xl text-yellow-600"></i>
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-600">特集記事</p>
                <p className="text-2xl font-bold text-gray-900">{featuredArticles.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-lg">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                <i className="ri-checkbox-circle-line text-2xl text-purple-600"></i>
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-600">選択中</p>
                <p className="text-2xl font-bold text-gray-900">{selectedArticles.length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="bg-white p-6 rounded-xl shadow-lg mb-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={handleSelectAll}
                className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                {selectedArticles.length === allArticles.length ? '全選択解除' : '全選択'}
              </button>
              <button
                onClick={handleDeleteSelected}
                disabled={selectedArticles.length === 0}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-50 cursor-pointer whitespace-nowrap"
              >
                選択した記事を削除
              </button>
            </div>
            
            {deleteMessage && (
              <div className={`px-4 py-2 rounded-lg ${
                deleteMessage.includes('削除しました') 
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-yellow-100 text-yellow-700'
              }`}>
                {deleteMessage}
              </div>
            )}
          </div>
        </div>

        {/* Articles Table */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900">記事一覧</h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    選択
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    記事
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    カテゴリー
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    著者
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    投稿日
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    閲覧数
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    種別
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {allArticles.map((article) => (
                  <tr key={article.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedArticles.includes(article.id)}
                        onChange={() => handleSelectArticle(article.id)}
                        disabled={!isUserArticle(article.id)}
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded disabled:opacity-50"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <img 
                          src={article.mainImage || article.image} 
                          alt={article.title}
                          className="w-12 h-12 object-cover rounded-lg mr-4"
                        />
                        <div>
                          <div className="text-sm font-medium text-gray-900 line-clamp-2">
                            {article.title}
                          </div>
                          <div className="text-sm text-gray-500 line-clamp-1">
                            {article.excerpt}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-600 rounded-full">
                        {article.category}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {article.author}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {article.date}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {article.views?.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        isUserArticle(article.id) 
                          ? 'bg-green-100 text-green-600' 
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {isUserArticle(article.id) ? 'ユーザー投稿' : 'システム記事'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md mx-4">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-delete-bin-line text-2xl text-red-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">記事を削除しますか？</h3>
              <p className="text-gray-600 mb-6">
                選択した{selectedArticles.length}件の記事を削除します。<br />
                この操作は取り消せません。
              </p>
              <div className="flex space-x-4">
                <button
                  onClick={cancelDelete}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 py-2 px-4 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  キャンセル
                </button>
                <button
                  onClick={confirmDelete}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 px-4 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  削除する
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
